/**
 * Consent glue for the OneTrust CMP.
 *
 * Google Consent Mode is handled by OneTrust's NATIVE Google Consent Mode
 * integration (enabled in the OneTrust portal), mirroring the live headless
 * frontend (ndh-2.0): it sets only the Consent Mode *default* (see
 * gtm-head.liquid) and lets OneTrust push the `consent update` from the visitor's
 * choice. This module therefore does NOT map OneTrust groups to gtag. It only:
 *   - optionally blocks page interaction until a consent choice is made, and
 *   - opens the OneTrust preference centre (footer link + external-media gate).
 *
 * Note: forwarding the OneTrust choice to Shopify's Customer Privacy API (needed
 * for the consent-gated checkout customer-events pixel) is handled separately in
 * assets/consent-shopify.js.
 *
 * External embeds (YouTube etc.) gate on OneTrust group C0004 (Targeting) in
 * media.js, matching the live frontend's video component.
 */
import { readJSON, hasConsentDecision } from '@theme/analytics-utils';

/** Lock scrolling/interaction until a consent choice exists. */
function maybeBlock() {
  const cfg = readJSON('analytics-consent'); // { blockUntilChoice }
  if (!cfg.blockUntilChoice) return;
  // Never lock inside the Shopify theme editor. Its cross-origin, cookie-
  // partitioned preview iframe prevents OneTrust from persisting the
  // `OptanonAlertBoxClosed` cookie, so `hasConsentDecision()` never turns true —
  // the lock would never release (no scrolling) and the banner would re-open on
  // every navigation. The merchant is editing here, so blocking is pointless.
  if (window.Shopify && window.Shopify.designMode) return;
  // `OnetrustActiveGroups.length` is not a reliable "decided" signal (C0001 is
  // always present and the list populates on SDK load). Use the real decision.
  if (hasConsentDecision()) return; // decided on a previous visit

  document.documentElement.classList.add('consent-locked');
  const unlock = () => {
    // ot:consent / OneTrustGroupsUpdated also fire on SDK load — only release the
    // lock once the visitor has actually made a choice.
    if (!hasConsentDecision()) return;
    document.documentElement.classList.remove('consent-locked');
    window.removeEventListener('ot:consent', unlock);
    window.removeEventListener('OneTrustGroupsUpdated', unlock);
  };
  window.addEventListener('ot:consent', unlock);
  window.addEventListener('OneTrustGroupsUpdated', unlock);

  const onChoice = () => document.documentElement.classList.remove('consent-locked');
  if (window.OneTrust && typeof window.OneTrust.OnConsentChanged === 'function') {
    window.OneTrust.OnConsentChanged(onChoice);
  } else {
    window.addEventListener('ot:consent', () => {
      if (window.OneTrust && typeof window.OneTrust.OnConsentChanged === 'function') {
        window.OneTrust.OnConsentChanged(onChoice);
      }
    }, { once: true });
  }
}

/** Open the OneTrust preferences/banner (used by footer link + media gate). */
export function openConsent() {
  try {
    if (window.OneTrust && typeof window.OneTrust.ToggleInfoDisplay === 'function') {
      window.OneTrust.ToggleInfoDisplay();
      return true;
    }
  } catch (error) {
    /* fall through */
  }
  return false;
}

function init() {
  maybeBlock();

  // Delegated opener: any element with [data-cookie-settings] reopens the banner.
  document.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target && typeof target.closest === 'function' && target.closest('[data-cookie-settings]')) {
      if (openConsent()) event.preventDefault();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
